import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  MapPin,
  Phone,
  Mail,
  Clock2,
  FileText,
  HeartPulse,
  ClipboardList,
  CalendarDays,
  Stethoscope,
  Globe2,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import web_logo from '../assets/images/web_logo.png';
import './PrescriptionTemplate.css';
import { buildApiUrl } from '../services/apiHelpers';

const safeText = (value, fallback = '—') => {
  if (value === null || value === undefined) return fallback;
  const str = String(value).trim();
  return str === '' ? fallback : str;
};

const buildClinicAddress = (address, city, state) => {
  const parts = [address, city, state].filter(part => part && String(part).trim() !== '');
  return parts.length ? parts.join(', ') : 'Address not available';
};

const formatAgeGender = (age, gender) => {
  const ageText = age !== null && age !== undefined && age !== '' ? String(age) : '—';
  const genderText = safeText(gender, '—');
  return `${ageText}/${genderText}`;
};

const formatClinicTiming = (value) => {
  if (!value && value !== 0) return ['—'];
  if (Array.isArray(value)) return value.map(String);
  const strValue = String(value);
  const parts = strValue.split(/\s*\|\s*|[,;]+/).map(part => part.trim()).filter(Boolean);
  return parts.length ? parts : ['—'];
};

const getFullDayName = (day) => {
  if (!day && day !== 0) return '';
  const normalized = String(day).trim();
  const map = {
    mon: 'Monday',
    tue: 'Tuesday',
    wed: 'Wednesday',
    thu: 'Thursday',
    fri: 'Friday',
    sat: 'Saturday',
    sun: 'Sunday'
  };
  const key = normalized.toLowerCase().slice(0, 3);
  return map[key] || normalized.replace(/^[a-z]/, (m) => m.toUpperCase());
};

const parseMedicineEntry = (item) => {
  if (typeof item === 'string') {
    return { name: item.trim() };
  }

  if (!item || typeof item !== 'object') {
    return { name: '' };
  }

  const name = item.name || item.medicineName || item.drugName || item.medName || item.medication || item.title || '';
  const form = item.form || item.medForm || item.type || item.dosageForm || '';
  const strength = item.strength || item.doseStrength || item.dosageStrength || item.mg || item.quantity || '';
  const morning = item.morning ?? item.m ?? item.doseMorning ?? item.dosesMorning ?? item.day1 ?? '';
  const afternoon = item.afternoon ?? item.a ?? item.doseAfternoon ?? item.dosesAfternoon ?? item.day2 ?? '';
  const evening = item.evening ?? item.e ?? item.doseEvening ?? item.dosesEvening ?? item.day3 ?? '';
  const night = item.night ?? item.n ?? item.doseNight ?? item.dosesNight ?? item.day4 ?? '';
  const duration = item.duration || item.days || item.durationDays || item.course || '';
  const timing = item.timing || item.schedule || item.frequency || item.freq || '';
  const instructions = item.instructions || item.instruction || item.notes || item.remarks || item.advice || '';

  return {
    ...item,
    name,
    form,
    strength,
    morning,
    afternoon,
    evening,
    night,
    duration,
    timing,
    instructions: [instructions, timing].filter(Boolean).join(' • ')
  };
};

const parseMedicines = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.map(parseMedicineEntry);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map(parseMedicineEntry);
      }
      if (parsed && typeof parsed === 'object') {
        if (Array.isArray(parsed.medicines)) {
          return parsed.medicines.map(parseMedicineEntry);
        }
        return [parseMedicineEntry(parsed)];
      }
    } catch (e) {
      const lines = trimmed.split(/\n|;|\|/).map(line => line.trim()).filter(Boolean);
      if (lines.length > 1) {
        return lines.map(line => parseMedicineEntry({ name: line }));
      }
      return [parseMedicineEntry({ name: trimmed })];
    }
  }

  if (typeof value === 'object') {
    if (Array.isArray(value.medicines)) {
      return value.medicines.map(parseMedicineEntry);
    }
    return [parseMedicineEntry(value)];
  }

  return [];
};

const parseClinicTimings = (value) => {
  if (!value) return {};

  let schedule = value;
  if (typeof schedule === 'string') {
    const trimmed = schedule.trim();
    if (!trimmed) return {};
    try {
      schedule = JSON.parse(trimmed || '{}');
    } catch (e) {
      return { Schedule: trimmed };
    }
  }

  if (typeof schedule !== 'object' || Array.isArray(schedule)) return {};

  const formatSession = (session) => {
    if (!session || typeof session !== 'object') return '';
    const from = session.from || '';
    const fromMinute = session.fromMinute ? String(session.fromMinute).padStart(2, '0') : '';
    const fromPeriod = session.fromPeriod ? ` ${session.fromPeriod}` : '';
    const to = session.to || '';
    const toMinute = session.toMinute ? String(session.toMinute).padStart(2, '0') : '';
    const toPeriod = session.toPeriod ? ` ${session.toPeriod}` : '';
    const fromStr = from ? `${from}${fromMinute ? `:${fromMinute}` : ''}${fromPeriod}` : '';
    const toStr = to ? `${to}${toMinute ? `:${toMinute}` : ''}${toPeriod}` : '';
    if (fromStr && toStr) return `${fromStr} - ${toStr}`;
    if (fromStr) return fromStr;
    if (toStr) return toStr;
    return '';
  };

  return Object.entries(schedule).reduce((acc, [day, info]) => {
    if (!info || typeof info !== 'object') {
      acc[day] = String(info || '—');
      return acc;
    }

    if (info.holiday) {
      acc[day] = 'Closed';
      return acc;
    }

    const sessions = [];
    ['session1', 'session2', 'session3', 'session4'].forEach((key) => {
      const sessionText = formatSession(info[key]);
      if (sessionText) sessions.push(sessionText);
    });

    const joined = sessions.filter(Boolean).join(' | ');
    acc[day] = joined || (info.description ? String(info.description) : '—');
    return acc;
  }, {});
};

const getClinicHeaders = (clinicId) => {
  const headers = {};
  const clinicValue = clinicId ?? (typeof window !== 'undefined' && window.__CLINIC_ID__) ?? null;
  if (clinicValue) headers['X-Clinic-Id'] = String(clinicValue);
  return headers;
};

const fetchJson = async (url, headers = {}) => {
  try {
    const res = await fetch(url, Object.keys(headers).length ? { headers } : undefined);
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      console.warn(`Failed to fetch ${url}: ${res.status} ${text}`);
      return null;
    }
    return await res.json();
  } catch (error) {
    console.warn(`Failed to fetch ${url}`, error);
    return null;
  }
};

const fetchPatientById = async (patientId, clinicId) => {
  if (!patientId) return null;
  return fetchJson(`/api/clinic/patients/id/${patientId}`, getClinicHeaders(clinicId));
};

const fetchPatientByPhone = async (clinicId, phone) => {
  if (!clinicId || !phone) return null;
  const safePhone = encodeURIComponent(String(phone).trim());
  const data = await fetchJson(`/api/clinic/patients/${clinicId}/phone/${safePhone}`, getClinicHeaders(clinicId));
  return Array.isArray(data) ? data[0] || null : data;
};

const fetchClinicById = async (clinicId) => {
  if (!clinicId) return null;
  return fetchJson(`/api/settings/clinic-profile/${clinicId}`, getClinicHeaders(clinicId));
};

const fetchDoctorById = async (doctorId, clinicId = null) => {
  if (!doctorId) return null;
  return fetchJson(`/api/doctors/${doctorId}`, getClinicHeaders(clinicId));
};

const fetchDoctorByClinicAndId = async (clinicId, doctorId) => {
  if (!doctorId) return null;
  const doctor = await fetchDoctorById(doctorId, clinicId);
  if (doctor) return doctor;

  if (!clinicId) return null;
  try {
    const res = await fetch(`/api/doctors/by-clinic-id/${clinicId}`, { headers: getClinicHeaders(clinicId) });
    if (!res.ok) return null;
    const doctors = await res.json();
    return Array.isArray(doctors)
      ? doctors.find(d =>
          Number(d.id) === Number(doctorId) ||
          Number(d.doctorId) === Number(doctorId) ||
          Number(d.doctor_id) === Number(doctorId)
        )
      : null;
  } catch (error) {
    console.warn(`Failed to fetch doctor ${doctorId} for clinic ${clinicId}`, error);
    return null;
  }
};

const normalizeVitalsPayload = (raw = {}) => {
  if (!raw || typeof raw !== 'object') return {};
  if (Array.isArray(raw)) {
    if (raw.some((item) => item?.measurementKey || item?.measurement_key)) {
      const result = { measurements: [] };
      raw.forEach((item) => {
        let value = item.value ?? item.value_json;
        if (typeof value === 'string') {
          try { value = JSON.parse(value); } catch {}
        }
        const measurementKey = item.measurementKey ?? item.measurement_key;
        const measurement = {
          id: item.id,
          measurementKey,
          name: item.name ?? item.measurementName ?? item.measurement_name ?? measurementKey,
          unit: item.unit ?? item.unitSnapshot ?? item.unit_snapshot ?? '',
          canonicalNumericValue: item.canonicalNumericValue ?? item.canonical_numeric_value ?? null,
          value,
        };
        result.measurements.push(measurement);
        if (measurementKey === 'm001' && value && typeof value === 'object') {
          result.bloodPressureSystolic ??= value.systolic;
          result.bloodPressureDiastolic ??= value.diastolic;
          result.bloodPressureUnit ??= measurement.unit;
        } else if (measurementKey === 'm002') { result.heartRate ??= value; result.heartRateUnit ??= measurement.unit; }
        else if (measurementKey === 'm003') { result.respiratoryRate ??= value; result.respiratoryRateUnit ??= measurement.unit; }
        else if (measurementKey === 'm004') { result.temperature ??= value; result.temperatureUnit ??= measurement.unit; }
        else if (measurementKey === 'm005') { result.spO2 ??= value; result.spO2Unit ??= measurement.unit; }
        else if (measurementKey === 'm006') { result.weight ??= value; result.weightUnit ??= measurement.unit; result.canonicalWeight ??= measurement.canonicalNumericValue; }
        else if (measurementKey === 'm007') { result.height ??= value; result.heightUnit ??= measurement.unit; result.canonicalHeight ??= measurement.canonicalNumericValue; }
        else if (measurementKey === 'm008') result.bmi ??= value;
        else if (measurementKey === 'm051') result.bloodSugar ??= value;
        else if (measurementKey === 'legacy-notes') result.notes ??= value;
      });
      return result;
    }
    return raw.length && typeof raw[0] === 'object' ? raw[0] : {};
  }

  const payload = raw.vitals || raw.latest || raw.data || raw;
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return {};

  if (raw.success === false && !raw.vitals && !raw.latest && !raw.data) {
    return {};
  }

  // Use exact backend field names from Java entity (camelCase)
  // Backend: temperature, heartRate, spO2, bloodPressureSystolic, bloodPressureDiastolic, bloodSugar, weight, height, bmi, respiratoryRate, notes
  return payload;
};

const fetchVitalsForPrescription = async (prescription = {}, headers = {}) => {
  if (!prescription) return {};

  const effectiveHeaders = Object.keys(headers).length ? headers : getClinicHeaders(prescription.clinicId);
  let vitals = {};

  try {
    if (prescription.appointmentId) {
      const doctorId = prescription.doctorId ?? prescription.doctor_id;
      const query = doctorId ? `?doctorId=${encodeURIComponent(doctorId)}` : '';
      const vRes = await fetch(`/api/vitals/appointments/${prescription.appointmentId}/measurements${query}`, { headers: effectiveHeaders });
      if (vRes.ok) {
        const vJson = await vRes.json();
        const extracted = normalizeVitalsPayload(vJson);
        if (Object.keys(extracted).length > 0) vitals = extracted;
      }
    }

    if ((!vitals || Object.keys(vitals).length === 0) && prescription.patientId) {
      const vRes = await fetch(`/api/vitals/patients/${prescription.patientId}/measurements`, { headers: effectiveHeaders });
      if (vRes.ok) {
        const vJson = await vRes.json();
        const appointmentRows = Array.isArray(vJson) && prescription.appointmentId
          ? vJson.filter((row) => String(row.appointmentId ?? row.appointment_id) === String(prescription.appointmentId))
          : vJson;
        const extracted = normalizeVitalsPayload(appointmentRows);
        if (Object.keys(extracted).length > 0) vitals = extracted;
      }
    }

    if ((!vitals || Object.keys(vitals).length === 0) && prescription.appointmentId) {
      const vRes = await fetch(`/api/vitals/appointment/${prescription.appointmentId}`, { headers: effectiveHeaders });
      if (vRes.ok) vitals = normalizeVitalsPayload(await vRes.json());
    }
    if ((!vitals || Object.keys(vitals).length === 0) && prescription.patientId) {
      const vRes = await fetch(`/api/vitals/latest/${prescription.patientId}`, { headers: effectiveHeaders });
      if (vRes.ok) vitals = normalizeVitalsPayload(await vRes.json());
    }
  } catch (error) {
    console.warn('Failed to fetch vitals for prescription', error);
  }

  return vitals;
};

const formatDisplayDate = (value) => {
  if (value === null || value === undefined || value === '') return '—';

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '—' : value.toLocaleDateString('en-IN');
  }

  const text = String(value).trim();
  if (!text) return '—';

  const parsed = new Date(text);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString('en-IN');
  }

  return text;
};

const normalizePrescriptionData = (sourceData = {}, fallback = {}) => {
  const data = sourceData && typeof sourceData === 'object' ? sourceData : {};
  const prescription = data.prescriptionRaw || data.prescription || data;
  const patient = data.patient || fallback.patient || {};
  const doctor = data.doctor || fallback.doctor || {};
  const clinicInfo = data.clinicInfo || data.clinic || fallback.clinicInfo || {};
  const sourceVitals = data.vitals || fallback.vitals || {};

  const clinicAddressLine = clinicInfo.addressLine || clinicInfo.address || clinicInfo.address_line;
  const clinicCity = clinicInfo.city || data.clinicCity || data.city;
  const clinicState = clinicInfo.stateName || clinicInfo.state || clinicInfo.stateCode || clinicInfo.state_name || data.clinicState || data.stateName || data.state;
  const clinicAddress = clinicAddressLine
    ? buildClinicAddress(clinicAddressLine, clinicCity, clinicState)
    : data.clinicAddress || buildClinicAddress(
        data.clinicAddressLine || data.addressLine || clinicInfo.addressLine || clinicInfo.address || clinicInfo.address_line,
        data.clinicCity || clinicInfo.city,
        data.clinicState || clinicInfo.stateName || clinicInfo.state || clinicInfo.stateCode || clinicInfo.state_name
      );

  const doctorName = doctor?.doctorName || doctor?.doctor_name || doctor?.name || data.doctorName || data.doctor_name || prescription?.doctorName || prescription?.doctor_name || (prescription?.doctorId ? `Dr. ${prescription.doctorId}` : 'Doctor');
  const doctorQualification = doctor?.qualification || data.doctorQualification || prescription?.doctorQualification || '';
  const doctorSpecialization = doctor?.specialization || data.doctorSpecialization || prescription?.doctorSpecialization || '';
  const doctorRegNo = doctor?.registrationNumber || doctor?.registration_number || doctor?.registration || doctor?.doctorRegistrationNumber || data.doctorRegNo || data.doctorRegistrationNumber || prescription?.doctorRegistrationNumber || prescription?.doctorRegNo || 'N/A';
  const doctorPhone = doctor?.doctorPhone || doctor?.doctor_phone || doctor?.phone || doctor?.contactNumber || doctor?.doctor_phone_number || data.doctorPhone || data.doctorMobile || prescription?.doctorMobile || prescription?.doctorPhone || 'Not available';

  const clinicName = data.clinicName || clinicInfo.clinicName || clinicInfo.clinic_name || prescription?.clinicName || prescription?.clinic_name || 'CLINIC NAME';
  const clinicTagline = data.clinicTagline || clinicInfo.tagline || clinicInfo.description || clinicInfo.clinicName || clinicInfo.clinic_name || '';
  const clinicPhone = clinicInfo.contactNumber || clinicInfo.contact_number || clinicInfo.phoneNumber || clinicInfo.phone || data.clinicPhone || data.clinicContactNumber || 'Not available';
  const clinicEmail = clinicInfo.email || clinicInfo.emailAddress || clinicInfo.email_address || data.clinicEmail || data.clinicEmailAddress || 'Not available';

  const patientName = data.patientName || patient?.patientName || patient?.patient_name || patient?.name || prescription?.patientName || prescription?.patient_name || 'Patient';
  const patientAge = data.patientAge ?? patient?.age ?? prescription?.patientAge ?? '';
  const patientGender = data.patientGender || patient?.gender || patient?.sex || prescription?.patientGender || prescription?.sex || 'Unknown';
  const patientContact = data.patientContact
    || data.patientPhone
    || data.patient_phone
    || patient?.patientPhone
    || patient?.patient_phone
    || patient?.phone
    || patient?.phoneNumber
    || patient?.contactNumber
    || patient?.mobile
    || patient?.mobileNumber
    || patient?.phone_number
    || patient?.contact_number
    || sourceVitals?.patientPhone
    || sourceVitals?.patient_phone
    || sourceVitals?.phone
    || sourceVitals?.phoneNumber
    || sourceVitals?.phone_number
    || sourceVitals?.contactNumber
    || sourceVitals?.mobile
    || sourceVitals?.mobileNumber
    || prescription?.patientContact
    || prescription?.patientPhone
    || prescription?.patient_phone
    || prescription?.phone
    || prescription?.phoneNumber
    || prescription?.phone_number
    || 'Not available';

  const diagnosis = data.diagnosis || prescription?.diagnosis || 'Not specified';
  const allergies = data.allergies || patient?.allergies || patient?.allergy || prescription?.allergies || prescription?.allergy || '—';
  const visitType = data.visitType || prescription?.prescriptionType || 'General';
  const createdAt = data.createdAt || prescription?.createdAt;
  const nextFollowUpDate = data.nextFollowUpDate || data.followUpDate || data.followupDate || data.followup || data.next_follow_up_date || prescription?.nextFollowUpDate || prescription?.followUpDate || prescription?.followupDate || prescription?.followup || prescription?.next_follow_up_date;
  const dateOnly = data.date || (createdAt ? formatDisplayDate(createdAt) : safeText(nextFollowUpDate, '—'));
  const rxId = data.rxId || data.prescriptionCode || prescription?.prescriptionCode || prescription?.id || 'N/A';
  const time = data.time || (createdAt ? (() => {
    const d = new Date(createdAt);
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const ampm = d.getHours() >= 12 ? 'PM' : 'AM';
    const hour12 = d.getHours() % 12 || 12;
    return `${String(hour12).padStart(2, '0')}:${minutes} ${ampm}`;
  })() : '');
  const date = time ? `${dateOnly}, ${time}` : dateOnly;
  const nextFollowUp = data.nextFollowUp || formatDisplayDate(nextFollowUpDate);
  const medicines = parseMedicines(data.medicines || prescription?.medicines || data.prescriptionMedicines || prescription?.prescriptionMedicines);
  const clinicalNotes = data.clinicalNotes || data.notes || prescription?.notes || prescription?.clinicalNotes || prescription?.instructions || '—';
  const doctorSignature = data.doctorSignature || doctor?.signatureUrl || doctor?.signature_url || null;
  const doctorId = data.doctorId || prescription?.doctorId;
  const appointmentId = data.appointmentId || prescription?.appointmentId;
  const vitals = normalizeVitalsPayload(data.vitals || (prescription && prescription.vitals) || sourceVitals || {});

  return {
    clinicName,
    clinicTagline,
    clinicAddress,
    clinicPhone,
    clinicEmail,
    doctorName,
    doctorQualification,
    doctorSpecialization,
    doctorRegNo,
    doctorPhone,
    clinicTimings: parseClinicTimings(data.clinicTimings || doctor?.defaultPrescriptionSchedule || doctor?.default_prescription_schedule),
    patientName,
    patientAge,
    patientGender,
    patientContact,
    diagnosis,
    allergies,
    visitType,
    date,
    rxId,
    time,
    nextFollowUp,
    vitals,
    medicines,
    clinicalNotes,
    doctorSignature,
    doctorId,
    appointmentId,
    prescriptionRaw: prescription
  };
};

const mapPrescriptionToDisplay = ({ prescription, patient = {}, doctor = {}, clinicInfo = {}, vitals = {}, initialData = {} }) => normalizePrescriptionData({
  ...initialData,
  prescription,
  patient,
  doctor,
  clinicInfo,
  vitals,
  prescriptionRaw: prescription
}, { patient, doctor, clinicInfo, vitals });

const PrescriptionTemplate = ({ data: initialData, prescriptionId }) => {
  const [data, setData] = useState(initialData || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (initialData) {
      const baseData = normalizePrescriptionData(initialData);
      setData(baseData);

      const shouldFetchEnrichment = Boolean(initialData.doctorId || initialData.patientId || initialData.clinicId || initialData.id || initialData.prescriptionId);
      if (!shouldFetchEnrichment) return;

      let cancelledInner = false;
      (async () => {
        try {
          const [patient, doctor] = await Promise.all([
            initialData.patientId ? fetchPatientById(initialData.patientId, initialData.clinicId) : null,
            initialData.doctorId ? fetchDoctorByClinicAndId(initialData.clinicId, initialData.doctorId) : null
          ]);

          if (cancelledInner) return;
          const clinic = initialData.clinicId ? await fetchClinicById(initialData.clinicId) : null;
          if (cancelledInner) return;

          let incomingVitals = normalizeVitalsPayload(initialData.vitals || (initialData.prescription && initialData.prescription.vitals) || {});
          if (Object.keys(incomingVitals).length === 0 && (initialData.appointmentId || initialData.patientId)) {
            const fetchedVitals = await fetchVitalsForPrescription(initialData, getClinicHeaders(initialData.clinicId));
            if (cancelledInner) return;
            incomingVitals = normalizeVitalsPayload(fetchedVitals || {});
          }

          setData(mapPrescriptionToDisplay({
            prescription: initialData,
            patient,
            doctor,
            clinicInfo: clinic,
            vitals: incomingVitals,
            initialData
          }));
        } catch (e) {
          console.warn('Failed to enrich initial prescription data', e);
        }
      })();

      return () => { cancelledInner = true; };
    }
    if (!prescriptionId) return;

    let cancelled = false;

    const fetchAll = async () => {
      setLoading(true);
      try {
        // 1) Fetch prescription
        const headers = getClinicHeaders();
        const rxRes = await fetch(`/api/prescriptions/${prescriptionId}`, { headers });
        if (!rxRes.ok) {
          const errorBody = await rxRes.text().catch(() => '');
          throw new Error(`Failed to fetch prescription: ${rxRes.status}${errorBody ? ` - ${errorBody}` : ''}`);
        }
        // The backend may return an object with { prescription, doctor }
        const rxJson = await rxRes.json();
        const prescription = rxJson && rxJson.prescription ? rxJson.prescription : rxJson;
        let responseDoctor = rxJson && rxJson.doctor ? rxJson.doctor : null;

        // 2) Fetch patient
        let patient = null;
        if (prescription.patientId) {
          patient = await fetchPatientById(prescription.patientId, prescription.clinicId);
        }
        if (!patient && prescription.clinicId) {
          const phone = prescription.patientPhone || prescription.patient_contact || prescription.phone || prescription.phoneNumber || prescription.phone_number;
          if (phone) {
            patient = await fetchPatientByPhone(prescription.clinicId, phone);
          }
        }

        // 3) Doctor info: prefer doctor data returned by the prescription endpoint,
        // otherwise fetch the clinic doctor list and pick the matching one
        let doctor = responseDoctor || null;
        if (!doctor && prescription.clinicId && prescription.doctorId) {
          doctor = await fetchDoctorByClinicAndId(prescription.clinicId, prescription.doctorId);
        }

        const vitals = await fetchVitalsForPrescription(prescription, headers);

        const clinicInfo = prescription.clinicId ? await fetchClinicById(prescription.clinicId) : null;

        const mapped = mapPrescriptionToDisplay({
          prescription,
          patient,
          doctor,
          clinicInfo,
          vitals
        });

        if (!cancelled) setData(mapped);
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || String(err));
          setLoading(false);
        }
      }
    };

    fetchAll();

    return () => { cancelled = true; };
  }, [initialData, prescriptionId]);

  if (!data && loading) return <div>Loading prescription...</div>;
  if (!data && error) return <div>Error loading prescription: {error}</div>;
  if (!data) return null;

  const {
    clinicName,
    clinicTagline,
    clinicAddress,
    clinicPhone,
    clinicEmail,
    doctorName,
    doctorQualification,
    doctorSpecialization,
    doctorRegNo,
    doctorPhone,
    clinicTimings = {},
    patientName,
    patientAge,
    patientGender,
    patientContact,
    diagnosis,
    date,
    rxId,
    nextFollowUp,
    vitals = {},
    medicines = [],
    clinicalNotes,
    doctorSignature
  } = data;

  const displayClinicName = safeText(clinicName, 'CLINIC NAME');
  const displayClinicTagline = safeText(clinicTagline, '');
  const displayClinicPhone = safeText(clinicPhone, 'Not available');
  const displayClinicEmail = safeText(clinicEmail, 'Not available');
  const displayPatientName = safeText(patientName, 'Patient');
  const displayPatientAgeGender = formatAgeGender(patientAge, patientGender);
  const displayPatientContact = safeText(patientContact, 'Not available');
  const displayDoctorName = safeText(doctorName, 'Doctor');
  const displayDoctorNameLabel = /^(dr\.?)/i.test(displayDoctorName) ? displayDoctorName : `Dr. ${displayDoctorName}`;
  const displayDoctorQualification = safeText(doctorQualification, '—');
  const displayDoctorSpecialization = safeText(doctorSpecialization, '—');
  const displayDoctorRegNo = safeText(doctorRegNo, 'N/A');
  const displayDoctorPhone = safeText(doctorPhone, 'Not available');
  const displayDiagnosis = safeText(diagnosis, 'Not specified');
  const displayClinicalNotes = safeText(clinicalNotes, '—');

  // Render rows dynamically based on medicine count; default to 1 empty row
  const totalRowsCount = Math.max(1, medicines.length);
  const emptyRowsNeeded = totalRowsCount - medicines.length;

  return (
    <div className="prescription-wrapper A4">
      {/* HEADER SECTION */}
      <header className="prescription-header">
        {/* Left Column: Clinic Info */}
        <div className="header-column clinic-info">
          <div className="clinic-branding">
            <h1 className="clinic-name">{displayClinicName}</h1>
            {displayClinicTagline && displayClinicTagline !== displayClinicName && (
              <p className="clinic-tagline">{displayClinicTagline}</p>
            )}
          </div>
          <div className="clinic-contact-details">
            <p className="contact-line"><MapPin className="icon-inline" />{clinicAddress}</p>
            <p className="contact-line"><Phone className="icon-inline" />{displayClinicPhone}</p>
            <p className="contact-line"><Mail className="icon-inline" />{displayClinicEmail}</p>
          </div>
        </div>

        {/* Center Column: Doctor Details */}
        <div className="header-column doctor-info">
          <div className="doctor-badge">CONSULTING DOCTOR</div>
          <h2 className="doctor-name">{displayDoctorNameLabel}</h2>
          <p className="doctor-qual">{displayDoctorQualification}</p>
          <p className="doctor-spec">{displayDoctorSpecialization}</p>
          <div className="doctor-divider"></div>
          <p className="doctor-reg">Reg. No: {displayDoctorRegNo}</p>
          <p className="doctor-phone">Phone: {displayDoctorPhone}</p>
        </div>

        {/* Right Column: Clinic Timings */}
        <div className="header-column clinic-timings-card">
          <div className="timings-header"><Clock2 className="icon-inline" />CLINIC TIMINGS</div>
          <div className="timings-body">
            {Object.entries(clinicTimings).map(([day, timeStr]) => {
              const fullDay = getFullDayName(day);
              const formatted = formatClinicTiming(timeStr);
              return (
                <div className="timing-row" key={day}>
                  <span className="timing-day">{fullDay}</span>
                  <span className={`timing-hours ${String(timeStr).toLowerCase() === 'closed' ? 'closed' : ''}`}>
                    {formatted.map((line, index) => (
                      <div key={index}>{line}</div>
                    ))}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </header>

      {/* PRESCRIPTION TITLE */}
      <div className="prescription-title-container">
        <div className="title-line-left"></div>
        <div className="prescription-title-badge"><FileText className="icon-inline" />PRESCRIPTION</div>
        <div className="title-line-right"></div>
      </div>

      {/* PATIENT INFORMATION */}
      <section className="patient-info-section">
        <div className="patient-info-grid">
          {/* Column 1 */}
          <div className="info-col column-avatar">
            <div className="avatar-container">
              <svg viewBox="0 0 24 24" className="avatar-svg">
                <path fill="#003380" d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.72 7.58,14 12,14Z" />
              </svg>
            </div>
            <div className="fields-group">
              <div className="info-row"><span className="label">Name</span><span className="sep">:</span><span className="val bold">{displayPatientName}</span></div>
              <div className="info-row"><span className="label">Age/Gender</span><span className="sep">:</span><span className="val">{displayPatientAgeGender}</span></div>
              <div className="info-row"><span className="label">Contact</span><span className="sep">:</span><span className="val">{displayPatientContact}</span></div>
            </div>
          </div>

          {/* Column 2 */}
          <div className="info-col column-diagnosis">
            <div className="diagnosis-icon-container">
              <Stethoscope className="diag-svg" />
            </div>
            <div className="fields-group">
              <div className="info-row"><span className="label">Diagnosis</span><span className="sep">:</span><span className="val bold text-blue">{displayDiagnosis}</span></div>
            </div>
          </div>

          {/* Column 3 */}
          <div className="info-col column-meta">
            <div className="meta-icon-container">
              <ClipboardList className="meta-svg" />
            </div>
            <div className="fields-group">
              <div className="info-row"><span className="label">Date</span><span className="sep">:</span><span className="val">{date}</span></div>
              <div className="info-row"><span className="label">Rx ID</span><span className="sep">:</span><span className="val">{rxId}</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* VITAL SIGNS */}
      <section className="vitals-section">
        <h3 className="section-title vital-signs-title"><HeartPulse className="icon-inline" />VITAL SIGNS</h3>
        <table className="vitals-table">
          <thead>
            <tr>
              <th>Height</th>
              <th>Weight</th>
              <th>BMI</th>
              <th>Temp</th>
              <th>HR</th>
              <th>BP</th>
              <th>Sugar</th>
              <th>SpO₂</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              {/* Order: Height, Weight, BMI, Temp, HR, BP, Sugar, SpO2 */}
              <td>{vitals.height != null ? `${vitals.height} ${vitals.heightUnit || 'cm'}` : '—'}</td>
              <td>{vitals.weight != null ? `${vitals.weight} ${vitals.weightUnit || 'kg'}` : '—'}</td>
              <td>{(() => {
                const bmiRaw = vitals.bmi;
                if (bmiRaw) return String(bmiRaw);
                try {
                  const weightSource = vitals.canonicalWeight ?? (vitals.weightUnit && vitals.weightUnit !== 'kg' ? null : vitals.weight);
                  const heightSource = vitals.canonicalHeight ?? (vitals.heightUnit && vitals.heightUnit !== 'cm' ? null : vitals.height);
                  const w = (weightSource ?? '').toString().replace(/[^0-9.]/g, '');
                  const h = (heightSource ?? '').toString().replace(/[^0-9.]/g, '');
                  const weightKg = parseFloat(w);
                  let heightM = parseFloat(h);
                  if (!Number.isFinite(weightKg) || !Number.isFinite(heightM)) return '—';
                  if (heightM > 3) heightM = heightM / 100;
                  const bmi = weightKg / (heightM * heightM);
                  if (!Number.isFinite(bmi)) return '—';
                  return Math.round(bmi * 10) / 10;
                } catch (e) { return '—'; }
              })()}</td>
              <td>{vitals.temperature != null ? `${vitals.temperature} ${vitals.temperatureUnit || '°C'}` : '—'}</td>
              <td>{vitals.heartRate != null ? `${vitals.heartRate} ${vitals.heartRateUnit || 'bpm'}` : '—'}</td>
              <td>{vitals.bloodPressureSystolic != null && vitals.bloodPressureDiastolic != null ? `${vitals.bloodPressureSystolic}/${vitals.bloodPressureDiastolic} ${vitals.bloodPressureUnit || 'mmHg'}` : '—'}</td>
              <td>{vitals.bloodSugar || '—'}</td>
              <td>{vitals.spO2 != null ? `${vitals.spO2} ${vitals.spO2Unit || '%'}` : '—'}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="8" className="vitals-notes-td">
                <strong>Notes:</strong> {vitals.notes || vitals.note || '—'}
              </td>
            </tr>
          </tfoot>
        </table>
        {Array.isArray(vitals.measurements) && vitals.measurements.some((item) => !['m001','m002','m003','m004','m005','m006','m007','m008','m051','legacy-notes'].includes(item.measurementKey)) && (
          <table className="vitals-table dynamic-vitals-table">
            <thead><tr><th>Additional measurement</th><th>Value</th></tr></thead>
            <tbody>{vitals.measurements.filter((item) => !['m001','m002','m003','m004','m005','m006','m007','m008','m051','legacy-notes'].includes(item.measurementKey)).map((item) => (
              <tr key={item.id ?? item.measurementKey}><td>{item.name}</td><td>{typeof item.value === 'object' ? JSON.stringify(item.value) : item.value} {item.unit}</td></tr>
            ))}</tbody>
          </table>
        )}
      </section>

      {/* MEDICINES TABLE */}
      <section className="medicines-section">
        <h3 className="section-title medicines-title">℞ MEDICINES</h3>
        <table className="medicines-table">
          <thead>
            <tr>
              <th className="col-num" rowSpan="2">#</th>
              <th className="col-med" rowSpan="2">Medicine + Form</th>
              <th className="col-strength" rowSpan="2">Strength</th>
              <th colSpan="4">Dosage</th>
              <th className="col-duration" rowSpan="2">Duration</th>
              <th className="col-instructions" rowSpan="2">Instructions</th>
            </tr>
            <tr className="sub-headers-row">
              <th className="dose-sub">Morning</th>
              <th className="dose-sub">Afternoon</th>
              <th className="dose-sub">Evening</th>
              <th className="dose-sub">Night</th>
            </tr>
          </thead>
          <tbody>
            {medicines.map((med, index) => (
              <tr key={index} className="med-data-row">
                <td className="center-text">{index + 1}</td>
                <td className="text-left-align">
                  <div className="med-name-row">
                    <span className="med-name-text">{safeText(med.name, '—')}</span>
                    {med.form ? <span className="med-form-label">{med.form}</span> : null}
                  </div>
                </td>
                <td className="center-text">{safeText(med.strength, '—')}</td>
                <td className="center-text dose-val">{safeText(med.morning ?? med.m, '—')}</td>
                <td className="center-text dose-val">{safeText(med.afternoon ?? med.a, '—')}</td>
                <td className="center-text dose-val">{safeText(med.evening ?? med.e, '—')}</td>
                <td className="center-text dose-val">{safeText(med.night ?? med.n, '—')}</td>
                <td className="center-text">{safeText(med.duration, '—')}</td>
                <td className="center-text">{safeText(med.instructions, '—')}</td>
              </tr>
            ))}
            {Array.from({ length: emptyRowsNeeded }).map((_, idx) => (
              <tr key={`empty-${idx}`} className="med-empty-row">
                <td className="center-text">{medicines.length + idx + 1}</td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* NOTES */}
      <div className="notes-followup-container">
        <div className="notes-card-box">
          <h4 className="card-box-title"><ClipboardList className="icon-inline" />CLINICAL NOTES</h4>
          <div className="clinical-notes-content">
            <p style={{ margin: 0 }}>{displayClinicalNotes}</p>
          </div>
        </div>
      </div>

      {/* BOTTOM CARDS */}
      <footer className="prescription-bottom-cards">
        {/* Signature */}
        <div className="bottom-card signature-card">
          <div className="signature-area">
            {doctorSignature ? (
              <img src={doctorSignature} alt="Signature" className="signature-img" />
            ) : (
              <div className="signature-placeholder-text">{displayDoctorNameLabel}</div>
            )}
          </div>
          <div className="signature-line-accent"></div>
          <h5 className="doc-footer-name">{displayDoctorNameLabel}</h5>
          <p className="doc-footer-meta">{displayDoctorQualification}</p>
          <p className="doc-footer-meta">Reg: {displayDoctorRegNo}</p>
          <p className="doc-footer-auth">Authorized Practitioner</p>
          <p className="doc-footer-digital">
            NOTE: This is a digitally signed prescription<br />
            and does not require any physical signature
          </p>
        </div>

        {/* Reminders - two column: left = Dos, right = Don'ts */}
        <div className="bottom-card reminders-card">
          <h5 className="card-accent-header">DOs & Don'ts</h5>
          <div className="reminders-columns">
            <div>
              <div className="col-title dos-title">DOs</div>
              <ul className="reminders-list-items dos-list">
                <li><CheckCircle2 className="check-icon positive" /> Take as prescribed</li>
                <li><CheckCircle2 className="check-icon positive" /> Complete full course</li>
                <li><CheckCircle2 className="check-icon positive" /> Consult if symptoms worsen</li>
                <li><CheckCircle2 className="check-icon positive" /> Keep medicines away from kids</li>
              </ul>
            </div>
            <div>
              <div className="col-title donts-title">Don'ts</div>
              <ul className="reminders-list-items donts-list">
                <li><XCircle className="check-icon negative" /> Do not self-medicate</li>
                <li><XCircle className="check-icon negative" /> Do not skip doses</li>
                <li className="muted-item"><XCircle className="check-icon negative" /> Avoid sharing medicines</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Follow-Up */}
        <div className="bottom-card followup-card-bottom">
          <h5 className="card-accent-header"><CalendarDays className="icon-inline" />FOLLOW-UP</h5>
          <div className="followup-card-body followup-card-bottom-body">
            <p className="followup-lbl">Next Follow-up</p>
            <p className="followup-val-date">{nextFollowUp}</p>
            <div className="dashed-separator-line"></div>
            <p className="appointments-lbl">For Appointments</p>
            <p className="appointments-phone"><Globe2 className="appointments-link-icon" />patient.cliniqueue.in</p>
          </div>
        </div>
      </footer>

      {/* FOOTER */}
      <div className="prescription-system-footer">
        <div className="footer-web-bar">
          <div className="footer-bar-item footer-bar-left">
            <Globe2 className="icon-inline footer-icon" />
            <span>www.cliniqueue.in</span>
          </div>
          <div className="footer-bar-item footer-bar-center">
            <div className="footer-logo-block">
              <span className="footer-logo-caption">Clinic operations powered by</span>
              <img src={web_logo} alt="CliniQueue" className="footer-web_logo-img" />
            </div>
          </div>
          <div className="footer-bar-item footer-bar-right">
            <Mail className="icon-inline footer-icon" />
            <span>support@cliniqueue.in</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export const generatePrescriptionPDF = async (data) => {
  if (typeof document === 'undefined') {
    throw new Error('PDF generation requires a browser environment.');
  }

  // ============================================================
  // 1. Resolve prescription data
  // ============================================================
  let resolvedData = data;

  if ((typeof data === 'number' || typeof data === 'string') && Number(data) > 0) {
    const headers = getClinicHeaders();

    const res = await fetch(
      buildApiUrl(`prescriptions/${data}`),
      { headers }
    );

    if (!res.ok) {
      throw new Error('Failed to fetch prescription for PDF');
    }

    const rxJson = await res.json();

    const prescription =
      rxJson && rxJson.prescription
        ? rxJson.prescription
        : rxJson;

    const responseDoctor =
      rxJson && rxJson.doctor
        ? rxJson.doctor
        : null;

    let patient = null;

    try {
      if (prescription.patientId) {
        patient = await fetchPatientById(
          prescription.patientId,
          prescription.clinicId
        );
      }

      if (!patient && prescription.clinicId) {
        const phone =
          prescription.patientPhone ||
          prescription.patient_contact ||
          prescription.phone ||
          prescription.phoneNumber ||
          prescription.phone_number;

        if (phone) {
          patient = await fetchPatientByPhone(
            prescription.clinicId,
            phone
          );
        }
      }
    } catch (e) {
      console.warn(
        'Failed to fetch patient for PDF',
        e
      );
    }

    let doctor = null;

    try {
      doctor = responseDoctor || null;

      if (!doctor && prescription.doctorId) {
        doctor = await fetchDoctorById(
          prescription.doctorId,
          prescription.clinicId
        );
      }
    } catch (e) {
      console.warn(
        'Failed to fetch doctor for PDF',
        e
      );
    }

    const vitals =
      await fetchVitalsForPrescription(
        prescription,
        headers
      );

    const clinicInfo =
      prescription.clinicId
        ? await fetchClinicById(
            prescription.clinicId
          )
        : null;

    resolvedData = mapPrescriptionToDisplay({
      prescription,
      patient,
      doctor,
      clinicInfo,
      vitals
    });

  } else if (
    resolvedData &&
    typeof resolvedData === 'object' &&
    !Array.isArray(resolvedData)
  ) {

    const hasClinic = resolvedData.clinicId;
    const hasDoctor = resolvedData.doctorId;
    const hasPatient = resolvedData.patientId;

    if (hasClinic || hasDoctor || hasPatient) {
      let patient = null;

      if (hasPatient) {
        patient = await fetchPatientById(
          resolvedData.patientId,
          resolvedData.clinicId
        );
      }

      if (!patient && resolvedData.clinicId) {
        const phone =
          resolvedData.patientPhone ||
          resolvedData.patient_contact ||
          resolvedData.phone ||
          resolvedData.phoneNumber ||
          resolvedData.phone_number;

        if (phone) {
          patient = await fetchPatientByPhone(
            resolvedData.clinicId,
            phone
          );
        }
      }

      const [doctor, clinicInfo] =
        await Promise.all([
          hasDoctor
            ? fetchDoctorById(
                resolvedData.doctorId,
                resolvedData.clinicId
              )
            : null,

          hasClinic
            ? fetchClinicById(
                resolvedData.clinicId
              )
            : null
        ]);

      const vitals =
        await fetchVitalsForPrescription(
          resolvedData,
          getClinicHeaders(
            resolvedData.clinicId
          )
        );

      const existingVitals =
        resolvedData.vitals &&
        typeof resolvedData.vitals === 'object' &&
        Object.keys(resolvedData.vitals).length > 0
          ? resolvedData.vitals
          : null;

      resolvedData = {
        ...resolvedData,
        patient:
          resolvedData.patient || patient,
        doctor:
          resolvedData.doctor || doctor,
        clinicInfo:
          resolvedData.clinicInfo || clinicInfo,
        vitals:
          existingVitals || vitals
      };
    }
  }

  // ============================================================
  // 2. Preserve current date/time behavior
  // ============================================================
  const now = new Date();

  resolvedData = {
    ...resolvedData,

    date: formatDisplayDate(now),

    time: now.toLocaleTimeString(
      'en-IN',
      {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'Asia/Kolkata'
      }
    )
  };

  // ============================================================
  // 3. Create EXACT SAME prescription component
  // ============================================================
  const container =
    document.createElement('div');

  container.style.position = 'fixed';
  container.style.left = '-99999px';
  container.style.top = '0';
  container.style.width = '210mm';
  container.style.height = '297mm';
  container.style.padding = '0';
  container.style.margin = '0';
  container.style.background = '#ffffff';
  container.style.zIndex = '-1';
  container.style.overflow = 'hidden';

  document.body.appendChild(container);

  const root = createRoot(container);

  try {
    root.render(
      <PrescriptionTemplate
        data={resolvedData}
      />
    );

    // ==========================================================
    // 4. Wait for fonts and images
    // ==========================================================
    if (document.fonts?.ready) {
      try {
        await document.fonts.ready;
      } catch (e) {
        console.warn(
          'Font loading wait failed',
          e
        );
      }
    }

    await new Promise(resolve =>
      setTimeout(resolve, 500)
    );

    const images =
      Array.from(
        container.querySelectorAll('img')
      );

    await Promise.all(
      images.map(img => {
        if (img.complete) {
          return Promise.resolve();
        }

        return new Promise(resolve => {
          img.onload = resolve;
          img.onerror = resolve;
        });
      })
    );

    await new Promise(resolve =>
      setTimeout(resolve, 150)
    );

    // ==========================================================
    // 5. OPTIMIZED RENDERING
    // ==========================================================
    const scaleCandidates = [
      3.0,
      2.6,
      2.2,
      2.0
    ];

    const jpegQualityCandidates = [
      0.96,
      0.94,
      0.92,
      0.9,
      0.88
    ];

    const TARGET_SIZE = 500 * 1024;
    const MAX_SIZE = 600 * 1024;

    let finalBlob = null;
    let bestBlob = null;
    let bestQualityScore = -Infinity;

    for (let s = 0; s < scaleCandidates.length; s++) {
      const captureScale = scaleCandidates[s];

      const canvas = await html2canvas(container, {
        scale: captureScale,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: container.offsetWidth,
        height: container.offsetHeight,
        windowWidth: container.offsetWidth,
        windowHeight: container.offsetHeight,
        imageTimeout: 15000,
        allowTaint: false,
        scrollX: 0,
        scrollY: 0
      });

      for (let q = 0; q < jpegQualityCandidates.length; q++) {
        const quality = jpegQualityCandidates[q];

        const imgData = canvas.toDataURL('image/jpeg', quality);

        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4',
          compress: true
        });

        pdf.addImage(
          imgData,
          'JPEG',
          0,
          0,
          210,
          297,
          undefined,
          'MEDIUM'
        );

        const blob = pdf.output('blob');

        if (!bestBlob || blob.size < bestBlob.size) {
          bestBlob = blob;
        }

        const qualityScore = (captureScale * 10) + quality * 10;

        if (blob.size <= TARGET_SIZE) {
          if (!finalBlob || qualityScore > bestQualityScore) {
            finalBlob = blob;
            bestQualityScore = qualityScore;
          }
        }
      }
    }

    finalBlob = finalBlob || bestBlob;

    if (!finalBlob) {
      throw new Error('Unable to generate prescription PDF');
    }

    if (finalBlob.size > MAX_SIZE) {
      const sizeMB = (finalBlob.size / (1024 * 1024)).toFixed(2);
      throw new Error(
        `Generated prescription PDF is still too large (${sizeMB} MB).`
      );
    }

    console.log(
      `[Prescription PDF] Final size: ${(
        finalBlob.size / 1024
      ).toFixed(0)} KB`
    );

    const filename = `prescription_${Date.now()}.pdf`;

    return new File(
      [finalBlob],
      filename,
      {
        type: 'application/pdf'
      }
    );

  } finally {
    try {
      root.unmount();
    } catch (e) {
      console.warn(
        'Prescription React root cleanup failed',
        e
      );
    }

    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
  }
};

export default PrescriptionTemplate;